import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog"
import type { PageProps } from "@/types/inertia"

type UserDetail = {
  id: number
  name: string | null
  email: string
  admin: boolean
  timezone: string | null
  created_at: string
  updated_at: string
}

export default function AdminUserShow({ user }: { user: UserDetail }) {
  const { props } = usePage<PageProps>()
  const currentUser = props.current_user
  const isSelf = currentUser?.id === user.id

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)
  const [deleteProcessing, setDeleteProcessing] = React.useState(false)

  const handleDelete = () => {
    setDeleteProcessing(true)
    router.delete(`/admin/users/${user.id}`, {
      onFinish: () => {
        setDeleteProcessing(false)
        setDeleteDialogOpen(false)
      },
    })
  }

  const notice = props.flash?.notice
  const alert = props.flash?.alert

  return (
    <>
      <Head title={user.email}>
        <meta name="description" content={`User detail for ${user.email}.`} />
        <meta property="og:title" content={user.email} />
        <meta property="og:description" content={`User detail for ${user.email}.`} />
      </Head>
      <AppShell>
        <PageHeader
          title={user.name || user.email}
          description={
            <div className="space-y-1">
              {user.name && <p className="text-xs text-ink-muted">{user.email}</p>}
              <p className="text-xs text-ink-muted">User #{user.id}</p>
            </div>
          }
          backHref="/admin/users"
          backLabel="Back to users"
          actions={
            <div className="flex items-center gap-2">
              {user.admin && <Badge tone="accent">Admin</Badge>}
              <Button asChild variant="secondary" size="sm">
                <Link href={`/admin/users/${user.id}/edit`} className="no-underline">
                  Edit
                </Link>
              </Button>
              {!isSelf && (
                <Button variant="danger" size="sm" onClick={() => setDeleteDialogOpen(true)}>
                  Delete
                </Button>
              )}
            </div>
          }
        />

        {notice && <p className="mt-4 text-sm text-accent">{notice}</p>}
        {alert && <p className="mt-4 text-sm text-danger-display">{alert}</p>}

        <dl className="mt-6 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-[max-content_1fr]">
          <DetailRow label="Name" value={user.name ?? "—"} />
          <DetailRow label="Email" value={user.email} />
          <DetailRow label="Admin" value={user.admin ? "Yes" : "No"} />
          <DetailRow label="Timezone" value={user.timezone ?? "—"} />
          <DetailRow label="Created" value={formatDateTime(user.created_at)} />
          <DetailRow label="Updated" value={formatDateTime(user.updated_at)} />
        </dl>

        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle>Delete user?</DialogTitle>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <p className="text-sm text-ink-body">
                Are you sure you want to delete user <strong>{user.email}</strong>? This action cannot be undone.
              </p>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="secondary" type="button">Cancel</Button>
                </DialogClose>
                <Button
                  variant="danger"
                  type="button"
                  disabled={deleteProcessing}
                  onClick={handleDelete}
                >
                  {deleteProcessing ? "Deleting…" : "Yes, delete user"}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </AppShell>
    </>
  )
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-sm text-ink-muted">{label}</dt>
      <dd className="text-sm text-ink-body">{value}</dd>
    </>
  )
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
